'use client'
import { FC, MouseEvent, useMemo } from 'react'
import { Button } from '../../base/Button'
import { Img } from '../../base'
import styled from 'styled-components'
import { css } from 'styled-components'
import { mq, presets, rgbAlpha, vw } from '@/styles'
import type { TypeCmsRasterImage } from '@/schema'

type TypeSlideData = {
  title: string
  subtitle?: string
  link: { path: string; title: string }
  leftImage?: TypeCmsRasterImage
  centerImage?: TypeCmsRasterImage
  rightImage?: TypeCmsRasterImage
  media?: TypeCmsRasterImage
  mainType: 'product' | 'media'
  textPosition: 'left' | 'center' | 'right'
  textAlign: 'left' | 'center' | 'right'
  textValign: 'top' | 'center' | 'bottom' | string
  titleSize?: 'xl' | 'l' | 'm' | 's'
  subtitleSize?: 'l' | 'm' | 's'
}

type Props = {
  slide: TypeSlideData
  onNavigate: (path: string) => void
  index: number
}

const isRasterImage = (img: TypeCmsRasterImage): img is TypeCmsRasterImage => 'default' in img || 'id' in img
const getSlideImages = (images: TypeCmsRasterImage[]) => images.map((item, idx) => {
  const img = !item ? false : isRasterImage(item) ? item : false

  return img ? (
    <Img key={idx} lazy={false} media={item} className={`product-highlight-image-${['left', 'center', 'right'][idx]}`} />
  ) : (
    <div key={idx} className={`placeholder product-highlight-image-${['left', 'center', 'right'][idx]}`} />
  )
})
export const SlideContent: FC<Props> = ({ slide, onNavigate, index }) => {
  const { title, subtitle, link, leftImage, centerImage, rightImage, media, mainType, textPosition, textAlign, textValign, titleSize, subtitleSize } = slide
  const textAlignHorizontal = useMemo(() => textAlign?.toLowerCase(), [textAlign])
  const textAlignVertical = useMemo(() => textValign?.toLowerCase(), [textValign])
  const images = useMemo(() => mainType === 'product' ? [leftImage, centerImage, rightImage].filter(Boolean) : [media], [mainType, leftImage, centerImage, rightImage, media])
  const SlideImages = useMemo(() => getSlideImages(images), [images])
  const handleClick = (e: MouseEvent) => {
    e.stopPropagation()
    onNavigate(link.path)
  }

  return (
    <div className={`content-wrapper ${mainType === 'media' ? 'media' : ''}`} data-length={images.length} aria-labelledby={`highlight-title-${index}`}>
      {SlideImages}
      <HighlightsTextWrapper $position={textPosition?.toLowerCase() as 'left' | 'center' | 'right'} $textVAlign={textAlignVertical} $titleSize={titleSize} $subtitleSize={subtitleSize} className={`product-highlight-pos-${textPosition?.toLowerCase()}`} >
        <h2 id={`highlight-title-${index}`} className={`product-highlight-align-${textAlignHorizontal} product-highlight-text-title`} >
          {title}
        </h2>
        {subtitle && (
          <p className={`product-highlight-align-${textAlignHorizontal} product-highlight-text-subtitle`}>
            {subtitle}
          </p>
        )}
        <Button onClick={handleClick} className={`product-highlight-align-${textAlignHorizontal} product-highlight-text-button`}>
          {link.title}
        </Button>
      </HighlightsTextWrapper>
    </div>
  )
}

type DivProps = {
  $position: Props['slide']['textPosition']
  $textVAlign: Props['slide']['textValign']
  $titleSize?: Props['slide']['titleSize']
  $subtitleSize?: Props['slide']['subtitleSize']
}
const HighlightsTextWrapper = styled.div<DivProps>`
  grid-area: 1 / 2 / span 2 / span 4;
  text-align: center;
  width: 100%;
  ${({ $textVAlign }) => $textVAlign === 'top' ? css`padding-top: ${vw(20, 'mobile')}; align-self: start;` : $textVAlign === 'bottom' ? css`padding-bottom: ${vw(30, 'mobile')}; align-self: end;` : css`align-self: center;`};
  z-index: 1;

  ${mq.greaterThan('tablet')} {
    grid-area: ${({ $position }) => `1 / ${$position === 'left' ? 2 : $position === 'right' ? 7 : 3} / span 1 / span ${$position === 'center' ? 8 : 5}`};
    ${({ $textVAlign }) => $textVAlign === 'top' ? css`padding-top: ${vw(60, 'desktop')}; align-self: start;` : $textVAlign === 'bottom' ? css`padding-bottom: ${vw(70, 'desktop')}; align-self: end;` : css`align-self: center;`};
  }

  /* h2,
  p {
    text-shadow: 1px 1px 2px ${rgbAlpha('--color-black', .15)};
  } */

  h2 {
    ${({ $titleSize }) => $titleSize === 'xl' ? presets.font141 : $titleSize === 'l' ? presets.font113 : $titleSize === 'm' ? presets.font93 : presets.font53};
    color: var(--color-text);
    line-height: 90% !important;
  }

  p {
    ${({ $subtitleSize }) => $subtitleSize === 'l' ? css`${presets.font30_1} font-weight: 100;` : $subtitleSize === 'm' ? css`${presets.font30_1} font-weight: 100;` : presets.font15_1};
    color: var(--color-text);
    line-height: 120% !important;
    margin: ${vw(15, 'mobile')} 0 ${vw(20, 'mobile')};
  }

  h2,
  p,
  button {
    text-align: center;
  }

  ${mq.greaterThan('tablet')} {
    &.product-highlight-pos-left {
      p {
        &.product-highlight-align-left {
          margin: ${vw(15, 'desktop')} calc(100vw / 12) ${vw(30, 'desktop')} 0;
        }
        &.product-highlight-align-center {
          margin: ${vw(15, 'desktop')} calc(100vw / 24) ${vw(30, 'desktop')};
        }
        &.product-highlight-align-right {
          margin: ${vw(15, 'desktop')} 0 ${vw(30, 'desktop')} calc(100vw / 12);
        }
      }
    }

    &.product-highlight-pos-center {
      p {
        &.product-highlight-align-left {
          margin: ${vw(15, 'desktop')} calc(100vw / 12 * 4) ${vw(30, 'desktop')} 0;
        }
        &.product-highlight-align-center {
          margin: ${vw(15, 'desktop')} calc(100vw / 6) ${vw(30, 'desktop')};
        }
        &.product-highlight-align-right {
          margin: ${vw(15, 'desktop')} 0 ${vw(30, 'desktop')} calc(100vw / 12 * 4);
        }
      }
    }

    &.product-highlight-pos-right {
      p {
        &.product-highlight-align-left {
          margin: ${vw(15, 'desktop')} calc(100vw / 12) ${vw(30, 'desktop')} 0;
        }
        &.product-highlight-align-center {
          margin: ${vw(15, 'desktop')} calc(100vw / 24) ${vw(30, 'desktop')};
        }
        &.product-highlight-align-right {
          margin: ${vw(15, 'desktop')} 0 ${vw(30, 'desktop')} calc(100vw / 12);
        }
      }
    }

    .product-highlight-align-left { text-align: left; }
    .product-highlight-align-center { text-align: center; }
    .product-highlight-align-right { text-align: right; }

    button.product-highlight-align-left { margin: 0 auto 0 0; }
    button.product-highlight-align-right { margin: 0 0 0 auto; }

    p {
      ${({ $subtitleSize }) => $subtitleSize === 'l' ? css`${presets.font30_1} font-weight: 100;` : $subtitleSize === 'm' ? css`${presets.font26} font-weight: 100;` : presets.font20_3};

      &.home-header-text-subtitle {
        ${({ $subtitleSize }) => $subtitleSize === 'm' ? css`letter-spacing: -1.5px;` : ''};
      }
    }
  }

  ${mq.greaterThan('desktop')} {
      ${({ $textVAlign }) => $textVAlign === 'top' ? css`padding-top: 60px; align-self: start;` : $textVAlign === 'bottom' ? css`padding-bottom: 70px; align-self: end;` : css`align-self: center;`};

    &.product-highlight-pos-left {

      p {
        &.product-highlight-align-left {
          margin: 15px calc(100vw / 12) 30px 0;
        }
        &.product-highlight-align-center {
          margin: 15px calc(100vw / 24) 30px;
        }
        &.product-highlight-align-right {
          margin: 15px 0 30px calc(100vw / 12);
        }
      }
    }
    &.product-highlight-pos-center {

      p {
        &.product-highlight-align-left {
          margin: 15px calc(100vw / 12 * 4) 30px 0;
        }
        &.product-highlight-align-center {
          margin: 15px calc(100vw / 6) 30px;
        }
        &.product-highlight-align-right {
          margin: 15px 0 30px calc(100vw / 12 * 4);
        }
      }
    }
    &.product-highlight-pos-right {
      p {
        &.product-highlight-align-left {
          margin:15px calc(100vw / 12) 30px 0;
        }
        &.product-highlight-align-center {
          margin:15px calc(100vw / 24) 30px;
        }
        &.product-highlight-align-right {
          margin:15px 0 30px calc(100vw / 12);
        }
      }
    }

  }
`
